import { createSlice } from '@reduxjs/toolkit';
import { initializeSuccess } from '../actions';
import type { PaymentAvailabilityType } from '../shared/types';

export interface BookingConfigsState {
  directBookable: boolean;
  maxBookableServices: number;
  displayGalleryInBooking: boolean;
  displayFeedbackInBooking: boolean;
  minDaysBetweenFeedbackRequests: number;
  leadTimeMinutes: number;
  maxLeadTimeMinutes: number;
  cancelationTimeMinutes: number;
  intervalDuration: number;
  emailHintText: string | null;
  widgetHintText: string | null;
  collapseServiceCategories: boolean;
  displayNoPreferenceInBooking: boolean;
  requiredCapacity: boolean;
  requiredCapacityOptionsString: string | null;
  employeeRoomCombination: boolean;
  customerPayment: PaymentAvailabilityType;
  displayMerchantPickerInBooking: boolean;
  showInMerchantPicker: boolean;
  multiplyPriceByCapacity: boolean;
  displayMapsInBooking: boolean;
  shiftplanAvailability: boolean;
  hideEmployeeLastNameDuringBooking: boolean;
  shorepayOnlineActivatedAt: number | null;
  reviewLinks: {
    googleReviewUrl: string;
  };
}

const initialState: BookingConfigsState = {
  directBookable: false,
  maxBookableServices: 0,
  displayGalleryInBooking: false,
  displayFeedbackInBooking: false,
  minDaysBetweenFeedbackRequests: 0,
  leadTimeMinutes: 0,
  maxLeadTimeMinutes: 0,
  cancelationTimeMinutes: 0,
  intervalDuration: 0,
  emailHintText: '',
  widgetHintText: null,
  collapseServiceCategories: false,
  displayNoPreferenceInBooking: false,
  requiredCapacity: false,
  requiredCapacityOptionsString: null,
  employeeRoomCombination: false,
  customerPayment: 'mandatory',
  displayMerchantPickerInBooking: false,
  showInMerchantPicker: false,
  multiplyPriceByCapacity: false,
  displayMapsInBooking: false,
  shiftplanAvailability: false,
  hideEmployeeLastNameDuringBooking: false,
  shorepayOnlineActivatedAt: null,
  reviewLinks: {
    googleReviewUrl: '',
  },
};

const bookingConfigsSlice = createSlice({
  name: 'bookingConfigs',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(initializeSuccess, (state, action) => {
      const {
        direct_bookable: directBookable,
        max_bookable_services: maxBookableServices,
        display_gallery_in_booking: displayGalleryInBooking,
        display_feedback_in_booking: displayFeedbackInBooking,
        min_days_between_feedback_requests: minDaysBetweenFeedbackRequests,
        lead_time_minutes: leadTimeMinutes,
        max_lead_time_minutes: maxLeadTimeMinutes,
        cancelation_time_minutes: cancelationTimeMinutes,
        interval_duration: intervalDuration,
        email_hint_text: emailHintText,
        widget_hint_text: widgetHintText,
        collapse_service_categories: collapseServiceCategories,
        display_no_preference_in_booking: displayNoPreferenceInBooking,
        required_capacity: requiredCapacity,
        required_capacity_options_string: requiredCapacityOptionsString,
        employee_room_combination: employeeRoomCombination,
        customer_payment: customerPayment,
        display_merchant_picker_in_booking: displayMerchantPickerInBooking,
        show_in_merchant_picker: showInMerchantPicker,
        multiply_price_by_capacity: multiplyPriceByCapacity,
        display_maps_in_booking: displayMapsInBooking,
        shiftplan_availability: shiftplanAvailability,
        hide_employee_last_name_during_booking:
          hideEmployeeLastNameDuringBooking,
        review_links: { google_review_url: googleReviewUrl },
        shorepay_online_activated_at: shorepayOnlineActivatedAt,
      } = action.payload.bookingConfigs;

      return {
        directBookable,
        maxBookableServices,
        displayGalleryInBooking,
        displayFeedbackInBooking,
        minDaysBetweenFeedbackRequests,
        leadTimeMinutes,
        maxLeadTimeMinutes,
        cancelationTimeMinutes,
        intervalDuration,
        emailHintText,
        widgetHintText,
        collapseServiceCategories,
        displayNoPreferenceInBooking,
        requiredCapacity,
        requiredCapacityOptionsString,
        employeeRoomCombination,
        customerPayment,
        displayMerchantPickerInBooking,
        showInMerchantPicker,
        multiplyPriceByCapacity,
        displayMapsInBooking,
        shiftplanAvailability,
        hideEmployeeLastNameDuringBooking,
        shorepayOnlineActivatedAt,
        reviewLinks: {
          googleReviewUrl,
        },
      };
    });
  },
});

export const bookingConfigsReducer = bookingConfigsSlice.reducer;
